import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import { InsertRobot, InsertVehicle, InsertProduct, InsertReseller } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";

export function useRobots() {
  return useQuery({
    queryKey: [api.robots.list.path],
    queryFn: async () => {
      const res = await authFetch(api.robots.list.path);
      if (!res.ok) throw new Error("Failed to fetch robots");
      return res.json();
    },
  });
}

export function useRobot(id: string) {
  return useQuery({
    queryKey: [api.robots.get.path, id],
    queryFn: async () => {
      const url = buildUrl(api.robots.get.path, { id });
      const res = await authFetch(url);
      if (!res.ok) throw new Error("Failed to fetch robot");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateRobot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertRobot) => {
      const res = await authFetch(api.robots.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create robot");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.robots.list.path] }),
  });
}

export function useUpdateRobot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertRobot>) => {
      const url = buildUrl(api.robots.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update robot");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.robots.list.path] }),
  });
}

export function useDeleteRobot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const url = buildUrl(api.robots.delete.path, { id });
      const res = await authFetch(url, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to delete robot");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.robots.list.path] }),
  });
}

export function useVehicles() {
  return useQuery({
    queryKey: [api.vehicles.list.path],
    queryFn: async () => {
      const res = await authFetch(api.vehicles.list.path);
      if (!res.ok) throw new Error("Failed to fetch vehicles");
      return res.json();
    },
  });
}

export function useCreateVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertVehicle) => {
      const res = await authFetch(api.vehicles.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create vehicle");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.vehicles.list.path] }),
  });
}

export function useUpdateVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertVehicle>) => {
      const url = buildUrl(api.vehicles.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update vehicle");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.vehicles.list.path] }),
  });
}

export function useDeleteVehicle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/vehicles/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to delete vehicle");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.vehicles.list.path] }),
  });
}

export function useProducts() {
  return useQuery({
    queryKey: [api.products.list.path],
    queryFn: async () => {
      const res = await authFetch(api.products.list.path);
      if (!res.ok) throw new Error("Failed to fetch products");
      return res.json();
    },
  });
}

export function useProductImageResolver() {
  const { data: products = [] } = useProducts();
  return function resolveImage(sku?: string | null, fallback?: string | null): string | null {
    if (!sku) return fallback || null;
    const p = (products as any[]).find((pr: any) => pr.sku === sku);
    return p?.imageUrl || fallback || null;
  };
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertProduct) => {
      const res = await authFetch(api.products.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create product");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.products.list.path] }),
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertProduct>) => {
      const url = buildUrl(api.products.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update product");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.products.list.path] }),
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const url = buildUrl(api.products.delete.path, { id });
      const res = await authFetch(url, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to delete product");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.products.list.path] }),
  });
}

export function useResellers() {
  return useQuery({
    queryKey: [api.resellers.list.path],
    queryFn: async () => {
      const res = await authFetch(api.resellers.list.path);
      if (!res.ok) throw new Error("Failed to fetch resellers");
      return res.json();
    },
  });
}

export function useCreateReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertReseller) => {
      const res = await authFetch(api.resellers.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create reseller");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.resellers.list.path] }),
  });
}

export function useUpdateReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<InsertReseller>) => {
      const url = buildUrl(api.resellers.update.path, { id });
      const res = await authFetch(url, {
        method: "PUT",
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Impossible de mettre à jour le revendeur");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.resellers.list.path] }),
  });
}

export function useDeleteReseller() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const url = buildUrl(api.resellers.delete.path, { id });
      const res = await authFetch(url, { method: "DELETE" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to delete reseller");
      }
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [api.resellers.list.path] }),
  });
}
